import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useAxiosFetch from "../common/useAxiosFetch";
import LoadPage from "../common/LoadPage";
import { API_BASE_URL } from "../common/config.js";
import authHeader from "../services/auth-header";
import { CAMPAIGN_TONES, Pill } from "./mailingUi";

const TYPE_TONES = { reactivation: 'orange', newsletter: 'blue', trialReminder: 'gray', welcome: 'green' };

const HistoryTab = ({ refreshKey }) => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useAxiosFetch(`/api/mailings/campaigns?r=${refreshKey}`);
  const [details, setDetails] = useState(null);

  const isSending = data?.some((campaign) => campaign.status === 'sending');

  // Refresh while a campaign is still being sent
  useEffect(() => {
    if (!isSending) return undefined;
    const timer = setTimeout(refetch, 5000);
    return () => clearTimeout(timer);
  }, [isSending, data]);

  const openDetails = async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/mailings/campaigns/${id}`, { headers: authHeader() });
    if (response.ok) setDetails(await response.json());
  };

  if (loading && !data) return <LoadPage />;

  const detailStats = details ? [
    { key: 'recipients', tone: 'blue', value: details.stats?.recipients ?? 0 },
    { key: 'sent', tone: 'green', value: details.stats?.sent ?? 0 },
    { key: 'failed', tone: 'red', value: details.stats?.failed ?? 0 },
    { key: 'skipped_unsubscribed', tone: 'gray', value: details.stats?.skippedUnsubscribed ?? 0 },
  ] : [];

  return (
    <>
      <Modal show={Boolean(details)} onHide={() => setDetails(null)} size="lg">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{details?.subject || t(`mailing.type_${details?.type}`)}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {details && (
            <p className="mail-muted" style={{ marginTop: 0 }}>
              <Pill tone={TYPE_TONES[details.type]} plain>{t(`mailing.type_${details.type}_title`)}</Pill>
              {' '}{`${moment(details.createdAt).format('ll')} ${moment(details.createdAt).format('HH:mm')}`}{details.createdByName ? ` · ${details.createdByName}` : ''}
            </p>
          )}
          <div className="mail-detail-stats">
            {detailStats.map((stat) => (
              <div key={stat.key} className="mail-stat">
                <div>
                  <div className={`mail-stat-value`} style={{ color: `var(--mail-${stat.tone})` }}>{stat.value}</div>
                  <div className="mail-stat-label">{t(`mailing.${stat.key}`)}</div>
                </div>
              </div>
            ))}
          </div>
          {details?.trialGrantedEnvironments?.length > 0 && (
            <p><strong>{t('mailing.trials_granted_to')}:</strong> {details.trialGrantedEnvironments.join(', ')}</p>
          )}
          {details?.error && <div className="mail-status error"><i className="icon-alert" /> {details.error}</div>}
          {details?.recipients?.length > 0 ? (
            <div className="mail-table-wrap">
              <table className="mail-table">
                <thead>
                  <tr>
                    <td>{t('mailing.email')}</td>
                    <td>{t('environment')}</td>
                    <td>{t('mailing.language')}</td>
                    <td>{t('status')}</td>
                  </tr>
                </thead>
                <tbody>
                  {details.recipients.map((recipient, index) => (
                    <tr key={`${recipient.email}-${recipient.environment}-${index}`}>
                      <td>{recipient.email}</td>
                      <td>
                        {recipient.environment}
                        {recipient.stage && <span className="mail-tag">{t(`mailing.stage_${recipient.stage}`)}</span>}
                      </td>
                      <td>{recipient.language?.toUpperCase()}</td>
                      <td>
                        <Pill tone={CAMPAIGN_TONES[recipient.status]}>{t(`mailing.campaign_status_${recipient.status}`)}</Pill>
                        {recipient.error && <span className="mail-sub">{recipient.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : details && <div className="mail-empty">{t('mailing.no_recipients')}</div>}
        </Modal.Body>
      </Modal>

      <div className="mail-toolbar">
        <span className="mail-muted">{t('mailing.history_sub')}</span>
        <div className="mail-toolbar-spacer" />
        <button type="button" className="mail-link-btn" onClick={refetch}><i className="icon-refresh" /> {t('mailing.refresh')}</button>
      </div>

      {data?.length === 0 ? (
        <div className="mail-empty"><i className="icon-mail" style={{ color: 'var(--muted-text)' }} />{t('mailing.no_history')}</div>
      ) : (
        <div className="mail-table-wrap">
          <table className="mail-table">
            <thead>
              <tr>
                <td>{t('mailing.date')}</td>
                <td>{t('mailing.mailing')}</td>
                <td>{t('mailing.delivered')}</td>
                <td className="num">{t('mailing.skipped_unsubscribed')}</td>
                <td className="num">{t('mailing.trials_granted')}</td>
                <td>{t('status')}</td>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((campaign) => {
                const total = campaign.stats?.recipients ?? 0;
                const sent = campaign.stats?.sent ?? 0;
                const failed = campaign.stats?.failed || 0;
                return (
                  <tr key={campaign.id} className="clickable" onClick={() => openDetails(campaign.id)}>
                    <td className="nowrap">
                      {moment(campaign.createdAt).format('ll')}
                      <span className="mail-sub" title={campaign.createdByName ? `${t('mailing.by')}: ${campaign.createdByName}` : undefined}>
                        {moment(campaign.createdAt).format('HH:mm')}
                        {campaign.createdByName && ` · ${campaign.createdByName}`}
                      </span>
                    </td>
                    <td className="mail-subject-cell">
                      <strong title={campaign.subject || ''}>{campaign.subject || t(`mailing.type_${campaign.type}`)}</strong>
                      <span className="mail-sub">
                        {t(`mailing.type_${campaign.type}_title`)}
                        {campaign.stats?.environments != null && (
                          <span title={(campaign.environmentNames || []).join(', ')}> · {t('mailing.n_environments', { count: campaign.stats.environments })}</span>
                        )}
                        {campaign.trigger === 'scheduler' && <span className="mail-tag">{t('mailing.automatic')}</span>}
                      </span>
                    </td>
                    <td className="nowrap">
                      <strong>{sent}</strong> <span className="mail-muted">/ {total}</span>
                      {failed > 0 && <span style={{ color: 'var(--mail-red)', fontWeight: 700 }}> · {t('mailing.n_failed', { count: failed })}</span>}
                      <div className="mail-progress"><span style={{ width: `${total ? Math.round((sent / total) * 100) : 0}%` }} /></div>
                    </td>
                    <td className="num">{campaign.stats?.skippedUnsubscribed || 0}</td>
                    <td className="num">{campaign.stats?.trialsGranted || '–'}</td>
                    <td className="nowrap"><Pill tone={CAMPAIGN_TONES[campaign.status]}>{t(`mailing.campaign_status_${campaign.status}`)}</Pill></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default HistoryTab;
